import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { videosAPI, modulosAPI, cursosAPI } from '../services/api';
import { AppSidebar } from '../components/AppSidebar';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, Video, Upload, FileVideo, Clock, Hash, BookOpen } from 'lucide-react';

function CriarVideo() {
  const { id: cursoIdFromUrl } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    ordem: 1,
    duracao: '',
    curso_id: cursoIdFromUrl || '',
    modulo_id: '',
    video: null
  });
  const [cursos, setCursos] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [detectingDuration, setDetectingDuration] = useState(false);

  useEffect(() => {
    carregarCursos();
    if (cursoIdFromUrl) {
      carregarModulos(cursoIdFromUrl);
    }
  }, [cursoIdFromUrl]);

  useEffect(() => {
    if (formData.curso_id) {
      carregarModulos(formData.curso_id);
      // Limpar módulo selecionado ao trocar de curso
      setFormData(prev => ({
        ...prev,
        modulo_id: ''
      }));
    }
  }, [formData.curso_id]);

  const carregarCursos = async () => {
    try {
      const response = await cursosAPI.listar();
      setCursos(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar cursos:', err);
    }
  };

  const carregarModulos = async (cursoId) => {
    try {
      const response = await modulosAPI.listarPorCurso(cursoId);
      setModulos(response.data.modulos || []);
    } catch (err) {
      console.error('Erro ao carregar módulos:', err);
      setModulos([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const detectVideoDuration = (file) => {
    return new Promise((resolve) => {
      setDetectingDuration(true);
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = Math.round(video.duration);
        setDetectingDuration(false);
        resolve(duration);
      };
      
      video.onerror = () => {
        setDetectingDuration(false);
        resolve(null);
      };
      
      video.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        video: file
      }));

      // Detectar duração automaticamente
      const duration = await detectVideoDuration(file);
      if (duration) {
        setFormData(prev => ({
          ...prev,
          duracao: duration.toString()
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      let videoFileName = '';
      
      // Upload do vídeo primeiro, se houver
      if (formData.video) {
        setUploadProgress(25);
        const videoFormData = new FormData();
        videoFormData.append('video', formData.video);
        const videoResponse = await videosAPI.uploadVideo(videoFormData);
        videoFileName = videoResponse.data.arquivo;
        setUploadProgress(75);
      }

      // Criar registro do vídeo
      const videoData = {
        titulo: formData.titulo,
        descricao: formData.descricao,
        arquivo: videoFileName,
        curso_id: parseInt(formData.curso_id),
        modulo_id: formData.modulo_id ? parseInt(formData.modulo_id) : null,
        ordem: parseInt(formData.ordem) || 1,
        duracao: formData.duracao ? parseInt(formData.duracao) : null
      };

      console.log('Dados do vídeo sendo enviados:', videoData);
      await videosAPI.criar(videoData);
      setUploadProgress(100);
      setSuccess('Vídeo adicionado com sucesso!');
      
      // Redirecionar após 2 segundos
      setTimeout(() => {
        navigate(`/curso/${formData.curso_id}`);
      }, 2000);

    } catch (err) {
      setError(err.response?.data?.erro || 'Erro ao adicionar vídeo');
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const cursoSelecionado = cursos.find(curso => curso.id === parseInt(formData.curso_id));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <AppSidebar />
      <div className="ml-64 min-h-screen">
        {/* Header */}
        <div className="border-b border-slate-800/50 bg-slate-950/50 backdrop-blur-xl sticky top-0 z-40">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(formData.curso_id ? `/curso/${formData.curso_id}` : '/cursos')}
                className="text-slate-400 hover:text-white hover:bg-slate-800/50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {formData.curso_id ? 'Voltar ao Curso' : 'Voltar aos Cursos'}
              </Button>
              <div className="text-slate-400">/</div>
              <div className="flex items-center space-x-2">
                <Video className="w-4 h-4 text-red-400" />
                <span className="text-white font-medium">Adicionar Vídeo</span>
              </div>
              {cursoSelecionado && (
                <>
                  <div className="text-slate-400">/</div>
                  <div className="flex items-center space-x-2">
                    <BookOpen className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-400 font-medium">{cursoSelecionado.titulo}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl overflow-hidden">
              <div className="p-6 border-b border-slate-800/50">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center">
                    <Video className="w-5 h-5 text-red-400" />
                  </div>
                  Adicionar Vídeo ao Curso
                </h2>
                {cursoSelecionado && (
                  <p className="text-slate-400 mt-2">
                    Adicionando vídeo ao curso: <span className="text-blue-400 font-medium">{cursoSelecionado.titulo}</span>
                  </p>
                )}
              </div>
              
              <div className="p-6">
                {error && (
                  <div className="mb-6 p-4 bg-red-600/10 border border-red-600/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="mb-6 p-4 bg-green-600/10 border border-green-600/20 rounded-lg text-green-400 text-sm">
                    {success}
                  </div>
                )}

                {loading && uploadProgress > 0 && (
                  <div className="mb-6 p-4 bg-blue-600/10 border border-blue-600/20 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <Upload className="w-4 h-4 text-blue-400" />
                      <span className="text-blue-400 text-sm font-medium">Fazendo upload do vídeo...</span>
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-slate-400 text-xs mt-2">{uploadProgress}% concluído</p>
                  </div>
                )}

                {detectingDuration && (
                  <div className="mb-6 p-4 bg-yellow-600/10 border border-yellow-600/20 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Clock className="w-4 h-4 text-yellow-400" />
                      <span className="text-yellow-400 text-sm font-medium">Detectando duração do vídeo...</span>
                      <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="curso_id" className="block text-sm font-medium text-white mb-2">
                      <BookOpen className="w-4 h-4 inline mr-1" />
                      Curso *
                    </label>
                    <select
                      id="curso_id"
                      name="curso_id"
                      value={formData.curso_id}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600/50 transition-colors"
                    >
                      <option value="">Selecione um curso</option>
                      {cursos.map(curso => (
                        <option key={curso.id} value={curso.id}>
                          {curso.titulo}
                        </option>
                      ))}
                    </select>
                    {cursos.length === 0 && (
                      <p className="text-slate-400 text-xs mt-2">
                        Nenhum curso encontrado. Crie um curso primeiro.
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="modulo_id" className="block text-sm font-medium text-white mb-2">
                      Módulo (opcional)
                    </label>
                    <select
                      id="modulo_id"
                      name="modulo_id"
                      value={formData.modulo_id}
                      onChange={handleChange}
                      disabled={!formData.curso_id}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {!formData.curso_id ? 'Selecione um curso primeiro' : 'Nenhum módulo (vídeo avulso)'}
                      </option>
                      {modulos.map(modulo => (
                        <option key={modulo.id} value={modulo.id}>
                          {modulo.nome}
                        </option>
                      ))}
                    </select>
                    {formData.curso_id && modulos.length === 0 && (
                      <p className="text-slate-400 text-xs mt-2">
                        Nenhum módulo encontrado neste curso. Crie módulos primeiro.
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="titulo" className="block text-sm font-medium text-white mb-2">
                      Título do Vídeo *
                    </label>
                    <input
                      type="text"
                      id="titulo"
                      name="titulo"
                      value={formData.titulo}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors"
                      placeholder="Digite o título do vídeo"
                    />
                  </div>

                  <div>
                    <label htmlFor="descricao" className="block text-sm font-medium text-white mb-2">
                      Descrição
                    </label>
                    <textarea
                      id="descricao"
                      name="descricao"
                      rows={4}
                      value={formData.descricao}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors resize-none"
                      placeholder="Descreva o conteúdo do vídeo (opcional)"
                    />
                  </div>

                  <div>
                    <label htmlFor="video" className="block text-sm font-medium text-white mb-2">
                      <FileVideo className="w-4 h-4 inline mr-1" />
                      Arquivo de Vídeo *
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        id="video"
                        name="video"
                        accept="video/*"
                        onChange={handleFileChange}
                        required
                        className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-red-600 file:text-white hover:file:bg-red-700 file:cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors"
                      />
                    </div>
                    <p className="text-slate-400 text-xs mt-2">
                      Formatos aceitos: MP4, AVI, MOV, WMV, FLV (máx. 16MB)
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="ordem" className="block text-sm font-medium text-white mb-2">
                        <Hash className="w-4 h-4 inline mr-1" />
                        Ordem na Lista
                      </label>
                      <input
                        type="number"
                        id="ordem"
                        name="ordem"
                        value={formData.ordem}
                        onChange={handleChange}
                        min="1"
                        className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:border-red-600/50 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="duracao" className="block text-sm font-medium text-white mb-2">
                        <Clock className="w-4 h-4 inline mr-1" />
                        Duração
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          id="duracao"
                          name="duracao"
                          value={formData.duracao}
                          onChange={handleChange}
                          min="1"
                          readOnly
                          className="w-full px-4 py-3 bg-slate-800/30 border border-slate-700/50 rounded-lg text-slate-300 placeholder-slate-500 cursor-not-allowed"
                          placeholder="Detectada automaticamente"
                        />
                        {formData.duracao && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-slate-400">
                            {formatDuration(parseInt(formData.duracao))}
                          </div>
                        )}
                      </div>
                      <p className="text-slate-400 text-xs mt-2">
                        A duração será detectada automaticamente ao selecionar o vídeo
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(formData.curso_id ? `/curso/${formData.curso_id}` : '/cursos')}
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800/50"
                      disabled={loading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      disabled={loading || detectingDuration || !formData.curso_id || !formData.titulo}
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Adicionar Vídeo
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CriarVideo; 